/**
 * "Join waitlist" buttons scroll to the signup form instead of leaving the page.
 *
 * The header button and the banner button at the foot of the page both link to
 * `#join-waitlist`, a plain same-page anchor. On a page that has a signup form — the
 * home page and How It Works — that lands on the form with no JS at all, so the
 * visitor is never sent somewhere else while the script is still loading. This
 * upgrades the jump to a smooth scroll and puts the cursor in the email field.
 *
 * From a page with no form (About, Science, the policies) the anchor has nothing to
 * match, so the script sends them to the home page's form instead.
 *
 * Where the form settles is measured at click time rather than fixed in CSS, because
 * the sticky header is a different height on each page and width. The sections'
 * `scroll-margin-top` covers the same ground for the plain, scriptless jump.
 */
const TARGET_ID = 'join-waitlist';
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

function focusEmail(target, immediate) {
  const input = target.querySelector('input[type="email"]');
  if (!input) return;
  // preventScroll matters: focusing would otherwise jump the page and cut the
  // smooth scroll short.
  const focus = () => input.focus({ preventScroll: true });
  if (immediate) focus();
  else setTimeout(focus, 600);
}

/**
 * How far down the page the header covers.
 *
 * Measured from the header's own rows rather than its section box: on the home page
 * that box collapses to zero height (the pills sit over the hero), so trusting it put
 * the email field underneath them. The distance between the first row's top and the
 * last row's bottom is the same whether the header is pinned or scrolling away, which
 * makes this safe to call at any scroll position — 116px on desktop, 112 on a phone.
 */
function headerOffset() {
  const rows = document.querySelectorAll('.header__row');
  if (!rows.length) return 24;
  const first = rows[0].getBoundingClientRect();
  const last = rows[rows.length - 1].getBoundingClientRect();
  const rowsHeight = Math.max(0, last.bottom - first.top);
  // 24 for the header's inset from the top of the page, 24 to breathe.
  return Math.round(rowsHeight + 48);
}

/**
 * Whichever element actually scrolls this page.
 *
 * The theme only hands scrolling to `.page-wrapper` from 990px up; narrower than that
 * it leaves html and body scrolling normally and the wrapper just grows to fit. So the
 * wrapper is asked whether it scrolls rather than assumed to — assuming it did meant a
 * tap on a phone scrolled nothing at all.
 */
function scrollerFor(target) {
  const wrapper = target.closest('.page-wrapper');
  if (wrapper) {
    const overflow = getComputedStyle(wrapper).overflowY;
    const scrolls = overflow === 'auto' || overflow === 'scroll';
    if (scrolls && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
  }
  return document.scrollingElement || document.documentElement;
}

function scrollToForm(target, immediate) {
  const scroller = scrollerFor(target);
  // Scrolling the document goes through the window; scrolling the wrapper does not.
  const isDocument = scroller === document.scrollingElement || scroller === document.documentElement;
  const current = isDocument ? window.scrollY : scroller.scrollTop;
  const top = Math.max(0, target.getBoundingClientRect().top + current - headerOffset());
  // 'instant' rather than 'auto': the theme sets `scroll-behavior: smooth` on both the
  // wrapper and html, and 'auto' would defer to it — ignoring reduced motion.
  const options = { top, behavior: immediate ? 'instant' : 'smooth' };
  if (isDocument) window.scrollTo(options);
  else scroller.scrollTo(options);
  focusEmail(target, immediate);
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.(`a[href*="#${TARGET_ID}"]`);
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

  const target = document.getElementById(TARGET_ID);
  event.preventDefault();

  if (!target) {
    // No form on this page: send them to the one on the home page.
    window.location.href = `/#${TARGET_ID}`;
    return;
  }

  scrollToForm(target, REDUCED.matches);
  // Leave the address bar alone: the hash would otherwise stick around and re-jump
  // on a back navigation.
});

// Arriving from another page with the hash already in the URL.
if (window.location.hash === `#${TARGET_ID}`) {
  window.addEventListener(
    'load',
    () => {
      const target = document.getElementById(TARGET_ID);
      if (target) scrollToForm(target, true);
    },
    { once: true }
  );
}
